<template>
  <div class="account">
    <div class="page">
      <Main v-if="currentComponent === 'Main'"
        :nickname="nickname || ''"
        :bio="bio || ''"
        :plan-description="planDescription || ''"
        :default-card-brand="defaultCardObject ? defaultCardObject.brand : ''"
        :default-card-last4="defaultCardObject ? defaultCardObject.last4 : ''"
        :name="name || ''"
        :gender="gender || ''"
        :language-of-interest="languageOfInterest || ''"
        :hobby="hobby || ''"
        :email="email || ''"
        :member-code="memberCode || ''"
        :is-stripe-customer-id-present="!!(stripeCustomerId)"
        :show-registration-complete="showRegistrationCompleteOverlay"
        :is-i-phone="isIPhone"
        :campaign-id="campaignId || ''"
        :campaign-name="campaignName || ''"
        :campaign-start-date="campaignStartDate || ''"
        :campaign-end-date="campaignEndDate || ''"
        @open="(component, editPage) => {goToComponent(component, editPage)}"
        @logout="logout"
      />

      <EditInformation v-else-if="currentComponent === 'EditInformation'"
        :value-name="editPage"
        :original-value="getOriginalValueForEdit() || ''"
        :is-text-area="editPage === '自己紹介'"
        @save="newValue => {saveNewValue(newValue)}"
        @back="goToComponent('Main')"
      />

      <ChangePlan v-else-if="currentComponent === 'ChangePlan'"
        :plan-id="planId || ''"
        :plan-description="planDescription || ''"
        :payment-type="paymentType || ''"
        :campaign-id="campaignId || ''"
        :campaign-name="campaignName || ''"
        :campaign-start-date="campaignStartDate || ''"
        @back="goToComponent('Main')"
      />

      <PaymentMethod v-else-if="currentComponent === 'PaymentMethod'"
        :stripe="stripe"
        :is-default-card-set="defaultCardId !== null"
        :default-card-brand="defaultCardObject ? defaultCardObject.brand : ''"
        :default-card-last4="defaultCardObject ? defaultCardObject.last4 : ''"
        :credit-cards="creditCards"
        :is-new-trial-user="isNewTrialUser"
        :is-choose-plan-version="trialChoosePlanVersion"
        :is-i-phone="isIPhone"
        :is-campaign-trial="isCampaignTrial"
        :trial-type="trialType"
        @add="(cardElement, payPeriod, campaignId) => {addCreditCard(cardElement, payPeriod, campaignId)}"
        @set="idOfCardToSet => {setDefaultCard(idOfCardToSet)}"
        @back="goToComponent('Main')"
      />
    </div>
  </div>
</template>

<script>
import { 
  getMemberInfo,
  postMemberInfo,
  getStripeCustomerData,
  getListOfCards,
  getSetupIntent,
  setDefaultCard
} from '@/api/members.js';
import { startTrial } from '@/api/member_registration.js';

import Main from '@/components/account/Main.vue'
import EditInformation from '@/components/account/EditInformation.vue'
import ChangePlan from '@/components/account/ChangePlan.vue'
import PaymentMethod from '@/components/account/PaymentMethod.vue'
import { IS_UI_ONLY_MODE, CAMPAIGN_TRIAL_TYPE_KEY } from '../main';

export default {
  name: 'Account',

  components: {
    Main,
    EditInformation,
    ChangePlan,
    PaymentMethod
  },

  props: {
    userId: {
      type: String,
      required: true
    },

    loginToken: {
      type: String,
      required: true
    },

    isProduction: {
      type: Boolean,
      required: true
    },

    isLoaded: {
      type: Boolean,
      required: true
    },

    urlPath: {
      type: Array,
      required: true
    },

    stripe: {
      type: Object,
      required: true
    },

    isStripeDev: {
      type: Boolean,
      required: true
    },

    isIPhone: {
      type: Boolean,
      default: false
    },

    isCampaignSuccess: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentComponent: "Main",
      editPage: "",

      memberDetails: null,

      creditCards: [
        // {
        //   id,
        //   brand,
        //   last4
        // }
      ],
      defaultCardId: null,
      defaultCardObject: null,

      trialStartIntervalId: null,
      trialStartResult: false,
      trialStartAttempt: 0,

      showRegistrationCompleteOverlay: false
    }
  },

  computed: {
    nickname() {
      return this.memberDetails ? this.memberDetails.nickName : ""
    },

    bio() {
      return this.memberDetails ? this.memberDetails.biography : ""
    },

    planDescription() {
      return this.memberDetails ? this.memberDetails.planJapaneseName : ""
    },

    name() {
      return this.memberDetails ?
        `${this.memberDetails.familyName} ${this.memberDetails.firstName}` : ""
    },

    gender() {
      if (this.memberDetails) {
        const {gender} = this.memberDetails
        switch (gender) {
          case 1:
            return "男"
          case 2:
            return "女"
          default:
            // TODO: Handle all cases
            return gender
        }
      } else {
        return ""
      }
    },

    languageOfInterest() {
      if (this.memberDetails) {
        const {learnMethod} = this.memberDetails
        switch (learnMethod) {
          case 0:
            return "英語"
          default:
            // TODO: Handle all cases
            return learnMethod
        }
      } else {
        return ""
      }
    },

    hobby() {
      return this.memberDetails ? this.memberDetails.hobby : ""
    },

    email() {
      return this.memberDetails ? this.memberDetails.mail : ""
    },

    memberCode() {
      return this.memberDetails ? this.memberDetails.memberCode : ""
    },

    planId() {
      return this.memberDetails ? this.memberDetails.planId : ""
    },

    paymentType() {
      return this.memberDetails ? this.memberDetails.paymentType : ""
    },

    campaignId() {
      return this.memberDetails ? this.memberDetails.campaignId : ""
    },

    campaignName() {
      return this.memberDetails ? this.memberDetails.campaignName : ""
    },

    campaignStartDate() {
      return this.memberDetails ? this.memberDetails.campaignStartDate : ""
    },

    campaignEndDate() {
      return this.memberDetails ? this.memberDetails.campaignEndDate : ""
    },

    userStatus() {
      return this.memberDetails ? this.memberDetails.status : ""
    },

    managedByIAP() {
      return this.memberDetails ? this.memberDetails.managedByIAP : ""
    },

    initialTrialDate() {
      return this.memberDetails ? this.memberDetails.initialTrialDate : ""
    },

    isNewTrialUser() {
      return this.initialTrialDate === null && this.planId === 4 && this.managedByIAP === 0
    },

    stripeCustomerId() {
      return this.memberDetails ? (!this.isStripeDev ? this.memberDetails.stripeCustomerId : this.memberDetails.stripeCustomerIdDev) : ""
    },

    cardholderName() {
      const { firstRoma, familyRoma } = this.memberDetails
      return firstRoma && familyRoma ? `${firstRoma} ${familyRoma}` : undefined
    },

    paymentHelperParams() {
      return {
        customerId: this.stripeCustomerId,
        isProduction: !this.isStripeDev,
        userId: this.userId,
        token: this.loginToken
      }
    },

    trialStartErrorMessage() {
      return "無料体験の開始にエラーが発生しました。\nカスタマーサポートにお問い合わせください。"
    },

    trialChoosePlanVersion() {
      return this.memberDetails.status === 10 || this.isCampaignTrial
    },

    trialType() {
      return this.memberDetails ? this.memberDetails.trialType : ""
    },

    isCampaignTrial() {
      return (this.trialType === null || this.trialType === CAMPAIGN_TRIAL_TYPE_KEY) &&
        this.campaignId === null
    }
  },

  created() {
    if (!this.loaded && !this.memberDetails) {
      this.loadAccountPage()
    }
  },

  methods: {
    async getMemberInfo() {
      const {data, isSuccess} = await getMemberInfo(this.userId, this.loginToken)
      if (isSuccess) {
        this.memberDetails = data
      } else {
        this.logout()
      }
      return isSuccess
    },

    async getAllStripeData() {
      this.defaultCardId = await getStripeCustomerData(this.paymentHelperParams)
      const { creditCards, defaultCardObject } = await getListOfCards({
        ...this.paymentHelperParams,
        defaultCardId: this.defaultCardId
      })
      this.creditCards = creditCards
      this.defaultCardObject = defaultCardObject
      if (this.defaultCardId) {
        this.creditCards = this.creditCards.filter(obj => obj.id !== this.defaultCardId)
      }
    },

    async loadAccountPage() {
      this.$emit('processing', true)
      if (await this.getMemberInfo()) {
        this.$emit('loggedIn')

        if (this.stripeCustomerId) {
          await this.getAllStripeData()
        }

        this.goToUrlPath()
      }
      this.$emit('processing', false)
      this.$emit('loaded', true)
    },

    goToUrlPath() {
      if (this.urlPath.length > 0) {
        switch (this.urlPath[0] || "") {
          case "edit_information":
            if (this.urlPath.length > 1) {
              this.editPage = this.getEditPageName(this.urlPath[1])
              this.currentComponent = this.editPage !== "" ? "EditInformation" : "Main"
            }
            break
          // case "change_plan":
          //   this.currentComponent = "ChangePlan"
          //   break
          case "payment_method":
            this.currentComponent = "PaymentMethod"
            break
          default:
            this.currentComponent = "Main"
            break
        }   
      }
      if (this.isCampaignSuccess) {
        window.history.pushState("", "", "./")
        if (IS_UI_ONLY_MODE || ((this.userStatus === 5 || this.userStatus === 10) && this.planId !== 4)) {
          this.showRegistrationCompleteOverlay = true
          this.trialStartResult = true
        } else {
          window.location.reload()
        }
      } else if (this.isNewTrialUser) {
        this.goToComponent("PaymentMethod")
        // const trialEndDate = format(add(new Date(), {days: 7}), "yyyy年MM月dd日")
        // const trialMessage = `ライトプラン\n（オンライン英会話 毎日1セッション）\n金額 8,780円（税抜）\n\n無料体験終了日${trialEndDate}までに自動更新を停止すれば料金は一切請求されません。\n\nお支払い情報の入力\n今回のお支払い額は0円です。\n\n今後のお支払い方法を設定して下さい。\n${trialEndDate}まで無料\n`
        // this.$emit('alert', "ご利用プラン確認", trialMessage, true, true)

      } else if (this.stripeCustomerId && !this.defaultCardId) {
        this.goToComponent("PaymentMethod")
        const message = "支払い方法がまだ追加されてないようです。\n追加しましょう。"
        this.$emit('alert', "支払い方法", message, true, true)
      }
    },

    goToComponent(component, editPage) {
      this.currentComponent = component
      this.editPage = editPage || ''
    },

    getEditPageName(value) {
      switch (value) {
        case "nickname":
          return "ニックネーム"
        case "bio":
          return "自己紹介"
        default:
          return ""
      }
    },

    getOriginalValueForEdit() {
      switch (this.editPage) {
        case "ニックネーム":
          return this.nickname
        case "自己紹介":
          return this.bio
        default:
          return ""
      }
    },

    async saveNewValue(newValue) {
      var params = {}
      var detailsName = ""
      var paramsName = ""
      switch (this.editPage) {
        case "ニックネーム":
          detailsName = "nickName"
          paramsName = "nickname"
          break
        case "自己紹介":
          detailsName = "biography"
          paramsName = "bio"
          break
        default:
          break
      }
      params[paramsName] = newValue
      this.memberDetails[detailsName] = newValue
      this.$emit('processing', true)
      await postMemberInfo(this.userId, params)
      this.$emit('processing', false)
      this.goToComponent('Main')
    },

    async addCreditCard(cardElement, payPeriod, campaignId) {
      if (IS_UI_ONLY_MODE) {
        this.goToComponent('Main')
        await this.startTrial(payPeriod, campaignId)
        return
      }
      this.$emit('processing', true)
      const clientSecret = await getSetupIntent(this.paymentHelperParams)
      try {
        const {setupIntent, error} = await this.stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: this.cardholderName
              }
            }
          }
        )
        if (error) {
          const {message} = error
          this.$emit('alert', "エラー", message)
          this.$emit('processing', false)
        } else {
          if (setupIntent.status === 'succeeded') {
            await this.getAllStripeData()
            await this.setDefaultCard(this.creditCards[0].id)
            this.$emit('processing', false)
            this.goToComponent('Main')

            if (!this.isNewTrialUser) {
              this.$emit('alert', "Success", "登録することができました")

            } else {
              if (this.defaultCardId) {
                await this.startTrial(payPeriod, campaignId)
                // console.log("start trial result", this.trialStartResult)

              } else {
                this.$emit('alert', "エラー", this.trialStartErrorMessage)
              }
            }
          }
        }
      } catch (e) {
        this.$emit('alert', "エラー", "予期せぬエラーが発生しました。サポートまでご連絡ください。 https://lancul.com/contact")
        this.$emit('processing', false)
      }
    },

    async setDefaultCard(idOfCardToSet) {
      this.$emit('processing', true)
      await setDefaultCard(idOfCardToSet, this.paymentHelperParams)
      await this.getAllStripeData()
      this.$emit('processing', false)
    },

    logout() {
      this.memberDetails = null
      this.creditCards = []
      this.defaultCardId = null
      this.defaultCardObject = null
      this.$emit('logout')
    },

    async startTrial(payPeriod, campaignId) {
      // console.log("try starting the trial")
      // console.log(`userStatus: ${this.userStatus}, planId: ${this.planId}, planDescription: ${this.planDescription}`)
      // console.log("")
      this.$emit('processing', true, true, "トライアルを開始していますので、", "しばらくお待ちください。")
      await startTrial(this.userId, payPeriod, campaignId)

      this.trialStartIntervalId = window.setInterval(async () => {
        // console.log("beginning of loop", this.trialStartAttempt)
        await this.getMemberInfo()
        if (IS_UI_ONLY_MODE) {
          this.memberDetails.planId = 5
        }
        // console.log(`check member info attempt ${this.trialStartAttempt + 1}`)
        // const { userStatus, planId, planDescription } = this
        // console.log(`userStatus: ${userStatus}, planId: ${planId}, planDescription: ${planDescription}`)
        // console.log("")
        if ((this.userStatus === 5 || this.userStatus === 10) && this.planId !== 4) {
          this.showRegistrationCompleteOverlay = true
          this.trialStartResult = true
          if (this.isCampaignTrial) {
            window.history.pushState("", "", "success-cmp")
            window.location.reload()
            window.history.replaceState
          }
        }
        this.trialStartAttempt += 1
      }, 5000)

      setTimeout(() => {
        if (!this.trialStartResult) {
          if (this.trialStartIntervalId) {
            // console.log("cleared in timeout")
            window.clearInterval(this.trialStartIntervalId)
            this.$emit('processing', false)
          }
          this.$emit('alert', "エラー", this.trialStartErrorMessage)
          // console.log("took too long, exiting main function")
        }
      }, 15500);
    }
  },

  watch: {
    trialStartResult() {
      if (this.trialStartIntervalId) {
        // console.log("cleared in watch")
        window.clearInterval(this.trialStartIntervalId)
        this.$emit('processing', false)
      }
    }
  }
}
</script>

<style scoped>
.account .page {
  margin: 40px 0 60px;
  /* max-width: 375px; */
  /* margin: 0 auto; */
}

@media (max-width: 634px) {
  .account .page {
    margin: 40px 20px 60px;
    /* margin-top: 40px;
    margin-bottom: 60px; */
  }
}
</style>
